// ═══════════════════════════════════════════════════════════════
// EL CATÁLOGO DEPORTIVO, LEÍDO
//
// `/api/live/all-markets` devuelve un solo arreglo `sports[]` y, dentro
// de cada deporte, los partidos en curso conviven con los que arrancan
// más tarde. Medido contra staging el 2026-10-06: 206 eventos, 150 con
// `etapa: "Live"` y 56 con `etapa: "Prematch"`, repartidos en 67 torneos
// y 12 deportes. La pantalla los listaba a todos seguidos, así que el
// jugador veía un partido que empieza mañana entre dos que están en el
// minuto 60 — por eso "se ve raro".
//
// El servidor ya manda los dos campos que hacen falta para arreglarlo:
// `etapa` dice si el partido está en curso y `torneo` dice en qué liga
// juega. Nadie los miraba. Este módulo es solo esa lectura, y vive
// afuera de App.jsx —igual que betslipPicks.js y betBestActions.js—
// porque es lógica pura: así se prueba sin DOM, que es lo único que este
// repositorio puede ejecutar para estas pantallas.
// ═══════════════════════════════════════════════════════════════

// Las dos pestañas. Son constantes y no literales sueltos para que una
// pestaña escrita mal falle al importar, no en silencio al filtrar.
export const VIVO = "vivo";
export const PROXIMOS = "proximos";

// El valor que manda el servidor para "en curso". Cualquier otro valor
// —incluido uno que todavía no existe, como "Suspended"— cae en
// PROXIMOS: decirle al jugador que un partido está en curso cuando no lo
// sabemos lo haría apostar a una cuota que ya no existe.
const ETAPA_EN_CURSO = "live";

const DIACRITICOS = /[̀-ͯ]/g;

// Acentos fuera y todo a minúscula antes de comparar. El catálogo está
// en español y los 67 nombres de torneo están llenos de tildes; si la
// búsqueda las exigiera, "belgica" no encontraría "Bélgica Sub-21" y el
// jugador concluiría que el partido no está.
export function normalizarTexto(texto){
  return String(texto ?? "").normalize("NFD").replace(DIACRITICOS,"").toLowerCase().trim();
}

// En qué pestaña va un evento.
//
// `ahora` se recibe en vez de leerse adentro para que la función sea
// pura y se pueda probar: una función que llama a Date.now() por su
// cuenta no se puede fijar en una prueba.
export function etapaDeEvento(evento, ahora = Date.now()){
  const cruda = normalizarTexto(evento?.etapa);
  if(cruda === ETAPA_EN_CURSO) return VIVO;
  if(cruda) return PROXIMOS;
  // Sin `etapa` queda la hora de comienzo. Mandar un evento sin etapa
  // directo a "por empezar" escondería un partido ya arrancado en la
  // pestaña que el jugador no está mirando; comparar con la hora lo deja
  // donde corresponde. Hoy el catálogo manda `etapa` en los 206 eventos,
  // así que esto es la red, no el camino.
  const comienzo = Date.parse(evento?.commence_time || "");
  if(Number.isNaN(comienzo)) return PROXIMOS;
  return comienzo <= ahora ? VIVO : PROXIMOS;
}

// La liga del evento, o cadena vacía. Vacía y no `undefined` porque el
// valor se usa como clave de agrupación y como texto de un chip: un
// `undefined` ahí imprime "undefined" en la pantalla del jugador.
export function ligaDeEvento(evento){
  return String(evento?.torneo ?? "").trim();
}

// Un evento coincide si la consulta aparece en un equipo o en la liga.
// Las dos cosas ya vienen en la respuesta, así que buscar no pide nada
// al servidor: con 67 ligas, escribir el nombre es la única forma
// razonable de llegar a la que tiene un solo partido.
//
// Se leen `h`/`a` (los nombres del catálogo) y `home`/`away` (los que
// usan el carrito y ScreenLive) para que el mismo buscador sirva en los
// dos lados sin adaptar los datos antes.
export function coincideBusqueda(evento, consulta){
  const q = normalizarTexto(consulta);
  if(!q) return true;
  const campos = [evento?.h, evento?.home, evento?.a, evento?.away, ligaDeEvento(evento)];
  return campos.some(campo => normalizarTexto(campo).includes(q));
}

// Devuelve el catálogo con los mismos deportes y los eventos que pasan
// los filtros. Un deporte que queda sin eventos se cae: si no, el
// jugador leería un encabezado de deporte con nada debajo.
export function filtrarCatalogo(sports, filtros = {}, ahora = Date.now()){
  const { etapa = null, deporte = null, liga = null, busqueda = "" } = filtros;
  return (Array.isArray(sports) ? sports : [])
    .filter(s => !deporte || s?.name === deporte)
    .map(s => ({ ...s, events:(Array.isArray(s?.events) ? s.events : []).filter(ev =>
      (!etapa || etapaDeEvento(ev, ahora) === etapa) &&
      (!liga || ligaDeEvento(ev) === liga) &&
      coincideBusqueda(ev, busqueda)) }))
    .filter(s => s.events.length > 0);
}

// Cuántos eventos hay en cada pestaña. Va en el rótulo de la pestaña:
// "EN VIVO · 150" le dice al jugador que ahí está la oferta antes de
// tocar, que es lo que hoy no sabe.
export function contarPorEtapa(sports, ahora = Date.now()){
  const conteo = { [VIVO]:0, [PROXIMOS]:0 };
  for(const s of (Array.isArray(sports) ? sports : [])){
    for(const ev of (Array.isArray(s?.events) ? s.events : [])){
      conteo[etapaDeEvento(ev, ahora)] += 1;
    }
  }
  return conteo;
}

// Las ligas presentes, de la que más partidos tiene a la que menos.
//
// Con 67 ligas el orden ES el filtro. Una lista alfabética de 67
// casillas tapa la oferta: la liga que hoy tiene 18 partidos aparecería
// después de sesenta que tienen uno. Ordenadas por cantidad, las que
// realmente tienen oferta entran en el primer ancho de pantalla y el
// resto se alcanza escribiendo el nombre en el buscador.
//
// A igual cantidad, alfabético: sin ese desempate el orden bailaría
// entre recargas y el chip que el jugador buscaba cambiaría de lugar.
export function ligasDisponibles(sports){
  const porLiga = new Map();
  for(const s of (Array.isArray(sports) ? sports : [])){
    for(const ev of (Array.isArray(s?.events) ? s.events : [])){
      const liga = ligaDeEvento(ev);
      if(!liga) continue;
      porLiga.set(liga,(porLiga.get(liga) || 0) + 1);
    }
  }
  return [...porLiga.entries()]
    .map(([liga, eventos]) => ({ liga, eventos }))
    .sort((a,b) => b.eventos - a.eventos || a.liga.localeCompare(b.liga,"es"));
}

// Los deportes presentes, con su icono y su cantidad. Se arma sobre el
// catálogo ya filtrado, no sobre el completo: un chip que lleva a una
// lista vacía es una promesa falsa, y acá hay 12 deportes compitiendo
// por el ancho de un teléfono.
export function deportesDisponibles(sports){
  return (Array.isArray(sports) ? sports : [])
    .map(s => ({ name:s?.name, icon:s?.icon, eventos:(Array.isArray(s?.events) ? s.events : []).length }))
    .filter(s => s.eventos > 0);
}

// Los primeros partidos en curso, aplanados y listos para una lista
// corta —el widget "En vivo ahora" del inicio—, que no agrupa por
// deporte y necesita el icono y la liga pegados a cada evento.
//
// Vive acá y no en la pantalla porque es la misma lectura del catálogo
// que ya hacen Deportes y En Vivo: un solo lugar que sabe qué es un
// partido en curso. El inicio leía `/api/live/combined`, una fuente
// distinta que devolvía cero, y por eso mostraba su estado vacío con
// decenas de partidos jugándose.
//
// `event_id`, `sport_key` y `commence_time` viajan con el evento. Hoy
// desde el inicio no se apuesta —la tarjeta lleva a la pantalla En
// Vivo—, pero si algún día se apostara, la clave con la que la
// liquidación busca el boleto ya está donde tiene que estar: es el
// defecto que apareció en ScreenLive y costaba plata.
export function eventosEnVivo(sports, limite = 3, ahora = Date.now()){
  if(!(limite > 0)) return [];
  const planos = [];
  for(const s of filtrarCatalogo(sports, { etapa:VIVO }, ahora)){
    for(const ev of s.events){
      planos.push({
        ...ev,
        icon: s?.icon || "",
        liga: ligaDeEvento(ev) || s?.name || "",
        home: ev?.h || ev?.home || "",
        away: ev?.a || ev?.away || "",
        sport_key: ev?.sport_key ?? null,
      });
      if(planos.length >= limite) return planos;
    }
  }
  return planos;
}
